// Batch 0 (UX/IA overhaul) — pure pagination math.
//
// Every long list in the app (Cameras, Sites, Users, Tenants, Detections,
// Evidence…) either renders everything under a hard `limit` or silently drops
// rows past a cap. This computes the page window from (total, page, pageSize)
// so <Pagination> and <DataTable> share one correct implementation, including
// the limit/offset the backends already accept. Pure + clamped so it can't
// produce an out-of-range page.

export interface PageInfo {
  /** 1-based current page, clamped into [1, pageCount]. */
  page: number;
  pageSize: number;
  total: number;
  /** Total number of pages (>= 1, even when total is 0). */
  pageCount: number;
  /** 1-based index of the first row on this page (0 when total is 0). */
  from: number;
  /** 1-based index of the last row on this page (0 when total is 0). */
  to: number;
  /** offset for a server query (`?offset=`), always >= 0. */
  offset: number;
  hasPrev: boolean;
  hasNext: boolean;
}

export function paginate(
  total: number,
  page: number,
  pageSize: number,
): PageInfo {
  const size = Math.max(1, Math.floor(pageSize) || 1);
  const safeTotal = Math.max(0, Math.floor(total) || 0);
  const pageCount = Math.max(1, Math.ceil(safeTotal / size));
  const clamped = Math.min(Math.max(1, Math.floor(page) || 1), pageCount);
  const offset = (clamped - 1) * size;
  const from = safeTotal === 0 ? 0 : offset + 1;
  const to = Math.min(offset + size, safeTotal);
  return {
    page: clamped,
    pageSize: size,
    total: safeTotal,
    pageCount,
    from,
    to,
    offset,
    hasPrev: clamped > 1,
    hasNext: clamped < pageCount,
  };
}

/** Slice an in-memory array for client-side pagination using the same math. */
export function pageSlice<T>(items: T[], page: number, pageSize: number): T[] {
  const { offset, pageSize: size } = paginate(items.length, page, pageSize);
  return items.slice(offset, offset + size);
}
